import { FC, MouseEvent } from 'react'
import Tooltip from 'react-tooltip-lite'

import { Button as PacoButton } from '../../../../@paco/components'
import { CalendarTypeRadioList, IconButton } from '../../../../@paco/compositions'
import { CalendarTypeFormOption } from '../../../../@paco/compositions/CalendarNavRadioList/CalendarTypeRadioList'
import { ConnectedHelpTooltip } from '../../../../@paco/connectors'
import trans from '../../../../@paco/helpers/trans'
import { TimeModeType } from '../../../../@paco/types'

import SearchBar from '../../../../components/SearchBar/SearchBar'
import Icon from '../../../../components/style/Icon/Icon'
import { translate } from '../../../../helpers/translations/translator'
import { PayrollPeriodViewModel } from '../../../../models'
import CalendarToolbar from '../CalendarToolbar/CalendarToolbar'

import './CalendarAndConceptsToolbar.scss'

interface CalendarAndConceptsToolbarProps {
    canViewShiftConcepts: boolean;
    selectedCalendarType: CalendarTypeFormOption;
    deleteButtonId?: string;
    deleteButtonDisabled?: boolean;
    employeeSearch: string;
    timeMode: TimeModeType;
    payrollPeriods: PayrollPeriodViewModel[];
    onCalendarTypeChange: () => void;
    setStartAndEndDate: (selectedDate: Date, startDate: Date, endDate: Date) => void;
    onPublishButtonClick?: () => void;
    onDeleteButtonClick?: () => void;
    onSearchBarChange: (employeeSearch: string) => void;
    className?: string;
}

const CalendarAndConceptsToolbar: FC<CalendarAndConceptsToolbarProps> = ({
    canViewShiftConcepts,
    selectedCalendarType,
    deleteButtonId,
    deleteButtonDisabled = false,
    employeeSearch,
    timeMode,
    payrollPeriods,
    onCalendarTypeChange,
    setStartAndEndDate,
    onPublishButtonClick,
    onDeleteButtonClick,
    onSearchBarChange,
    className = '',
}) => {
    const onDeleteButtonDragOver = (e: MouseEvent) => {
        e.preventDefault()
    }

    const handleTodayButtonClick = (): void => {
        setStartAndEndDate(new Date(), new Date(), new Date())
    }

    return (
        <div className={`calendar-and-concepts-toolbar ${className}`}>
            <div className="calendar-and-concepts-toolbar__wrapper">
                <div className="calendar-and-concepts-toolbar__nav-buttons-wrapper">
                    {canViewShiftConcepts && (
                        <CalendarTypeRadioList
                            className="calendar-and-concepts-toolbar__nav-buttons"
                            onChange={onCalendarTypeChange}
                            selectedCalendarType={selectedCalendarType}
                        />
                    )}
                    <PacoButton
                        text={translate('common.toToday')}
                        onClick={handleTodayButtonClick}
                        className="calendar-and-concepts-toolbar__mobile-today-button"
                    />
                </div>
                <CalendarToolbar
                    className="calendar-and-concepts-toolbar__calendar-toolbar"
                    timeMode={timeMode}
                    payrollPeriods={payrollPeriods}
                    setStartAndEndDate={setStartAndEndDate}
                />
                <div className="calendar-and-concepts-toolbar__buttons-wrapper">
                    {!! deleteButtonId && (
                        <div className="calendar-and-concepts-toolbar__delete-button-tooltip-wrapper">
                            {/* @ts-ignore */}
                            <Tooltip
                                forceDirection
                                direction="up"
                                className="calendar-and-concepts-toolbar__delete-button-tooltip"
                                content={translate('pages.shifts.deleteSelectedShifts')}
                                isOpen={! deleteButtonDisabled}
                                distance={10}
                                arrowSize={6}
                                padding={7}
                            >
                                <button
                                    type="button"
                                    id={deleteButtonId}
                                    disabled={deleteButtonDisabled}
                                    className="calendar-and-concepts-toolbar__delete-button"
                                    onDragOver={onDeleteButtonDragOver}
                                    onClick={onDeleteButtonClick}
                                >
                                    <Icon kind="trash" className="calendar-and-concepts-toolbar__delete-button-icon" />
                                </button>
                            </Tooltip>
                            <ConnectedHelpTooltip
                                index={4}
                                videoSrc="/help/delete-concept-shift.webm"
                                route="calendar"
                                subTitle={trans('help.conceptsCalendar.removeConceptShift.title')}
                                text={trans('help.conceptsCalendar.removeConceptShift.text')}
                                title={trans('help.conceptsCalendar.title')}
                                showMobileInfoWarning
                                className="calendar-and-concepts-toolbar__delete-button-help-tooltip"
                            />
                        </div>
                    )}
                    {onPublishButtonClick && (
                        <div className="calendar-and-concepts-toolbar__publish-button-wrapper">
                            <IconButton
                                icon="file-upload"
                                text={translate('pages.shifts.publish')}
                                onClick={onPublishButtonClick}
                                className="calendar-and-concepts-toolbar__publish-button"
                                iconClassName="calendar-and-concepts-toolbar__publish-button-icon"
                            />

                            <ConnectedHelpTooltip
                                index={5}
                                route="calendar"
                                subTitle={trans('help.conceptsCalendar.publishConceptShifts.title')}
                                text={trans('help.conceptsCalendar.publishConceptShifts.text')}
                                title={trans('help.conceptsCalendar.title')}
                                className="calendar-and-concepts-toolbar__publish-button-help-tooltip"
                            />
                        </div>
                    )}
                    <SearchBar
                        className="calendar-and-concepts-toolbar__search-bar"
                        placeholder={translate('common.searchEmployee')}
                        startVal={employeeSearch}
                        onChange={onSearchBarChange}
                    />
                </div>
            </div>
        </div>
    )
}

export default CalendarAndConceptsToolbar
